import type {
    LayoutBlock,
    Grid,
    GridTile,
    GridTileMeta,
    GridLayout,
    SizedEntity,
    Size,
    Dimensions,
    GetId,
    GetScore,
    Penalize,
    Boost,
    GridMeta,
} from './types';

import mapValues from 'lodash/mapValues';

export const EMPTY_TILE = '.';

export const getMinMax = <T>(
    ratedEntities: T[],
    getScore: GetScore<T>
): { min: number; max: number } => {
    return ratedEntities.reduce<{ min: number; max: number }>(
        (acc, entity) => {
            const score = getScore(entity, ratedEntities);
            if (score < acc.min) {
                acc.min = score;
            }
            if (score > acc.max) {
                acc.max = score;
            }
            return acc;
        },
        { min: Infinity, max: -Infinity }
    );
};

export const getSizes = (_gridSize: number): Size[] => {
    return ['sm', 'md', 'lg'];
};

export const getSizedEntities = <T>(
    ratedEntities: T[],
    gridSize: number,
    getScore: GetScore<T>
): SizedEntity<T>[] => {
    const sizes = getSizes(gridSize);
    const { min, max } = getMinMax(ratedEntities, getScore);
    const segmentRange = (max - min) / sizes.length;

    // Equal scores → 0/0 = NaN → everything became 'sm' (1×1 MetaOnly on
    // mobile, no abstract). Use the middle size so tiles get Vanilla height.
    if (!Number.isFinite(segmentRange) || segmentRange === 0) {
        const midSize = sizes[Math.floor(sizes.length / 2)] || 'md';
        return ratedEntities.map<SizedEntity<T>>((entity) => ({
            entity,
            size: midSize,
            score: getScore(entity, ratedEntities),
        }));
    }

    return ratedEntities.map<SizedEntity<T>>((entity) => {
        const score = getScore(entity, ratedEntities);
        const segmentIdx = Math.min(
            Math.floor((score - min) / segmentRange),
            sizes.length - 1
        );
        return {
            entity,
            size: sizes[segmentIdx] || 'sm',
            score,
        };
    });
};

export const getSizeDelta = (
    width: number,
    height: number,
    baseWidth: number,
    baseHeight: number,
    gridSize: number
) => {
    const baseArea = baseWidth * baseHeight;
    const delta = (width * height - baseArea) / baseArea;
    const minDelta = 0;
    const maxDelta = Math.max(
        Math.abs((gridSize * 3 - baseArea) / baseArea),
        Math.abs((1 - baseArea) / baseArea)
    );
    const normalizedDelta = normalize(Math.abs(delta), maxDelta, minDelta);
    return normalizedDelta * Math.sign(delta);
};

export const getSizeDeltaBySize = (
    width: number,
    height: number,
    size: Size,
    gridSize: number
) => {
    const { baseWidth, baseHeight } = getBaseDimensions(gridSize, size);
    return getSizeDelta(width, height, baseWidth, baseHeight, gridSize);
};

const generateDimensions = (
    dimensions: Array<{ width: number; height: number }>,
    baseWidth: number,
    baseHeight: number,
    gridSize: number
): Dimensions[] => {
    return dimensions
        .filter(({ width, height }) => width > 0 && height > 0)
        .map<Dimensions>(({ width, height }) => ({
            width,
            height,
            delta: getSizeDelta(width, height, baseWidth, baseHeight, gridSize),
        }))
        .sort((a, b) => Math.abs(a.delta) - Math.abs(b.delta));
};

export const getBaseDimensions = (
    gridSize: number,
    size: Size
): { baseWidth: number; baseHeight: number } => {
    if (gridSize === 1) {
        switch (size) {
            case 'lg': {
                return {
                    baseWidth: 1,
                    baseHeight: 3,
                };
            }
            case 'md': {
                return {
                    baseWidth: 1,
                    baseHeight: 2,
                };
            }
            default: {
                return {
                    baseWidth: 1,
                    baseHeight: 1,
                };
            }
        }
    }

    if (gridSize === 2) {
        switch (size) {
            case 'lg': {
                return {
                    baseWidth: 2,
                    baseHeight: 3,
                };
            }
            case 'md': {
                return {
                    baseWidth: 2,
                    baseHeight: 2,
                };
            }
            default: {
                return {
                    baseWidth: 1,
                    baseHeight: 2,
                };
            }
        }
    }

    switch (size) {
        case 'lg': {
            return {
                baseWidth: gridSize,
                baseHeight: 3,
            };
        }
        case 'md': {
            return {
                baseWidth: Math.ceil(gridSize / 2),
                baseHeight: 2,
            };
        }
        default: {
            return {
                baseWidth: 1,
                baseHeight: 2,
            };
        }
    }
};

export const mapSizedEntityToDimensions = <T>(
    sizedEntity: SizedEntity<T>,
    gridSize: number,
    penalize?: Penalize<T>,
    boost?: Boost<T>
): Dimensions[] => {
    const { size, entity } = sizedEntity;

    const boostValue = boost?.(entity) ?? 'none';

    if (boostValue !== 'none') {
        const baseWidth = gridSize;
        const baseHeight = 3;
        const height = baseHeight;
        return Array.from({
            length: boostValue === 'relative' ? gridSize : 1,
        }).map((_, idx) => {
            const width = gridSize - idx;
            return {
                width,
                height,
                delta: getSizeDelta(
                    width,
                    height,
                    baseWidth,
                    baseHeight,
                    gridSize
                ),
                boosted: true,
            };
        });
    }

    if (penalize?.(entity)) {
        return [
            {
                width: 1,
                height: 1,
                delta: 0,
                penalized: true,
            },
        ];
    }

    const { baseWidth, baseHeight } = getBaseDimensions(gridSize, size);

    if (gridSize === 1) {
        switch (size) {
            case 'lg': {
                return generateDimensions(
                    [{ width: baseWidth, height: baseHeight }],
                    baseWidth,
                    baseHeight,
                    gridSize
                );
            }
            case 'md': {
                return generateDimensions(
                    [{ width: baseWidth, height: baseHeight }],
                    baseWidth,
                    baseHeight,
                    gridSize
                );
            }
            default: {
                return generateDimensions(
                    [{ width: baseWidth, height: baseHeight }],
                    baseWidth,
                    baseHeight,
                    gridSize
                );
            }
        }
    }

    if (gridSize === 2) {
        switch (size) {
            case 'lg': {
                return generateDimensions(
                    [
                        { width: baseWidth, height: baseHeight },
                        { width: baseWidth, height: baseHeight - 1 },
                        { width: baseWidth - 1, height: baseHeight },
                    ],
                    baseWidth,
                    baseHeight,
                    gridSize
                );
            }
            case 'md': {
                return generateDimensions(
                    [
                        { width: baseWidth, height: baseHeight },
                        { width: baseWidth - 1, height: baseHeight + 1 },
                        { width: baseWidth, height: baseHeight + 1 },
                    ],
                    baseWidth,
                    baseHeight,
                    gridSize
                );
            }
            default: {
                return generateDimensions(
                    [
                        { width: baseWidth, height: baseHeight },
                        { width: baseWidth, height: baseHeight - 1 },
                    ],
                    baseWidth,
                    baseHeight,
                    gridSize
                );
            }
        }
    }

    switch (size) {
        case 'lg': {
            return generateDimensions(
                [
                    { width: baseWidth, height: baseHeight },
                    { width: baseWidth - 1, height: baseHeight },
                    { width: baseWidth - 1, height: baseHeight - 1 },
                    { width: baseWidth - 2, height: baseHeight },
                ],
                baseWidth,
                baseHeight,
                gridSize
            );
        }
        case 'md': {
            return generateDimensions(
                [
                    { width: baseWidth, height: baseHeight },
                    { width: baseWidth - 1, height: baseHeight + 1 },
                    { width: baseWidth - 1, height: baseHeight },
                    { width: baseWidth, height: baseHeight + 1 },
                    { width: baseWidth + 1, height: baseHeight },
                ],
                baseWidth,
                baseHeight,
                gridSize
            );
        }
        default: {
            return generateDimensions(
                [
                    { width: baseWidth, height: baseHeight },
                    { width: baseWidth, height: baseHeight - 1 },
                ],
                baseWidth,
                baseHeight,
                gridSize
            );
        }
    }
};

export const getLayouts = <T>(
    entity: SizedEntity<T>,
    getId: GetId<T>,
    gridSize = Infinity,
    penalize?: Penalize<T>,
    boost?: Boost<T>
): LayoutBlock[] => {
    return mapSizedEntityToDimensions(entity, gridSize, penalize, boost).map(
        (dimensions) => ({
            id: getId(entity.entity),
            size: entity.size,
            score: entity.score,
            ...dimensions,
        })
    );
};

export const isTileFilled = (tile: GridTile): tile is string => {
    return typeof tile === 'string' && tile !== EMPTY_TILE;
};

export const convertGridToString = (grid: Grid): string => {
    const stringifiedRows: string[] = [];
    for (let rowIdx = 0; rowIdx < grid.layout.length; rowIdx++) {
        const row = grid.layout[rowIdx];
        const rowString = row.join(' ');
        stringifiedRows.push(rowString);
    }
    return stringifiedRows.join('\n');
};

export const getGridTemplateAreas = (grid: Grid): string => {
    const stringifiedRows: string[] = [];
    for (let rowIdx = 0; rowIdx < grid.layout.length; rowIdx++) {
        const row = grid.layout[rowIdx];
        const rowString = row.join(' ');
        stringifiedRows.push("'" + rowString + "'");
    }
    return stringifiedRows.join(' ');
};

export const getUniqueTileIds = (grid: Grid): string[] => {
    return Object.keys(grid.meta.tiles);
};

export type GetGridStylesOptions = {
    rowHeight?: string;
    width?: string;
    gap?: string;
    /** Floor for gridAutoRows minmax (default 300px). */
    minRowHeight?: string;
    /** Cap for gridAutoRows minmax (default 600px). */
    maxRowHeight?: string;
};

export const getGridStyles = (
    grid: Grid,
    opts: GetGridStylesOptions
): Record<string, string> => {
    if (isSingleTileGrid(grid)) {
        return {
            flex: '1',
            flexDirection: 'row',
        };
    } else {
        const {
            rowHeight = '300px',
            width = 'auto',
            gap = '1rem',
            minRowHeight = '300px',
            maxRowHeight = '600px',
        } = opts || {};
        const columnCount = getColumnCount(grid);
        return {
            display: 'grid',
            width,
            gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
            gridAutoRows: `minmax(max(min(${rowHeight}, ${maxRowHeight}), ${minRowHeight}),auto)`,
            gridGap: gap,
            gridTemplateAreas: getGridTemplateAreas(grid),
        };
    }
};

export const getNextPlacement = (grid: Grid): [number, number] => {
    for (let i = 0; i < grid.layout.length; i++) {
        for (let j = 0; j < grid.layout[i].length; j++) {
            if (grid.layout[i][j] === EMPTY_TILE) {
                return [i, j];
            }
        }
    }
    return [grid.layout.length, 0];
};

export const getEmptyGridRow = (columnCount: number): string[] => {
    return Array.from({ length: columnCount }, () => EMPTY_TILE);
};

export const getEmptyGrid = (columnCount: number): Grid => {
    return {
        layout: [getEmptyGridRow(columnCount)],
        meta: {
            tiles: {},
            emptyTileCount: columnCount,
        },
    };
};

export const cloneGrid = (grid: Grid): Grid => {
    const newGridLayout: GridLayout = [];
    for (let r = 0; r < grid.layout.length; r++) {
        newGridLayout[r] = [...grid.layout[r]];
    }
    return {
        layout: newGridLayout,
        meta: {
            ...grid.meta,
            tiles: grid.meta.tiles
                ? Object.entries(grid.meta.tiles).reduce<
                      Record<string, GridTileMeta>
                  >((cloned, [id, tile]) => {
                      cloned[id] = { ...tile };
                      return cloned;
                  }, {})
                : grid.meta.tiles,
        },
    };
};

export const getColumnCount = (grid: Grid): number => {
    return grid.layout[0].length;
};

export const getRowCount = (grid: Grid): number => {
    return grid.layout.length;
};

export const addArticleToGrid = (
    grid: Grid,
    articleLayout: LayoutBlock
): Grid => {
    const columnCount = getColumnCount(grid);
    const { id, width, height } = articleLayout;
    const [r, c] = getNextPlacement(grid);

    if (c + width > columnCount) {
        throw new Error('article does not fit: overflow');
    }

    const newGrid = cloneGrid(grid);

    for (let h = 0; h < height; h++) {
        if (!newGrid.layout[r + h]) {
            newGrid.layout[r + h] = getEmptyGridRow(columnCount);
            newGrid.meta.emptyTileCount =
                newGrid.meta.emptyTileCount + columnCount;
        }
        for (let w = 0; w < width; w++) {
            const tile = newGrid.layout[r + h][c + w];
            if (isTileFilled(tile)) {
                throw new Error('article does not fit: overlap');
            }
            newGrid.layout[r + h][c + w] = id;
            newGrid.meta.emptyTileCount = newGrid.meta.emptyTileCount - 1;
        }
    }

    newGrid.meta = {
        ...newGrid.meta,
        tiles: {
            ...newGrid.meta.tiles,
            [articleLayout.id]: {
                ...articleLayout,
                startRow: r,
                startCol: c,
            },
        },
    };

    return newGrid;
};

export const normalize = (val: number, max: number, min: number): number => {
    return (val - min) / (max - min);
};

export const countMisalignments = (
    grid: Grid
): { count: number; tileIds: string[] } => {
    let misalignments = 0;
    const tileIds: string[] = [];

    const gridLayout = grid.layout;
    for (let i = 0; i < gridLayout.length - 1; i++) {
        if (
            isTileFilled(gridLayout[i][0]) &&
            isTileFilled(gridLayout[i + 1][0]) &&
            gridLayout[i][0] !== gridLayout[i + 1][0]
        ) {
            for (let j = 1; j < gridLayout[i].length; j++) {
                if (
                    isTileFilled(gridLayout[i][j]) &&
                    gridLayout[i][j] === gridLayout[i + 1][j]
                ) {
                    misalignments++;
                    tileIds.push(gridLayout[i][j] as string);
                    break;
                }
            }
        }
    }
    return { count: misalignments, tileIds };
};

export const getBadnessFromMisalignments = (grid: Grid): number => {
    const getBadness = (m: number) => m;

    const misalignmentCount = countMisalignments(grid).count;
    const maxMisalignmentCount = 3;

    return normalize(
        getBadness(misalignmentCount),
        getBadness(maxMisalignmentCount),
        getBadness(0)
    );
};

export const getBadnessFromEmptyTiles = (grid: Grid): number => {
    const getBadness = (s: number) => Math.log2(0.8 * s + 1);

    const maxEmptyTileCount = 3;
    const emptyTileCount = grid.meta.emptyTileCount;

    return normalize(
        getBadness(emptyTileCount),
        getBadness(maxEmptyTileCount),
        getBadness(0)
    );
};

export const getBadnessFromSizeDelta = (grid: Grid): number => {
    const tiles = Object.values(grid.meta.tiles);
    const avgDelta =
        tiles.reduce((deltaSum, tile) => deltaSum + Math.abs(tile.delta), 0) /
        tiles.length;
    return avgDelta;
};

export const evaluateGrid = (grid: Grid): number => {
    const badness =
        0.35 * getBadnessFromEmptyTiles(grid) +
        0.4 * getBadnessFromMisalignments(grid) +
        0.25 * getBadnessFromSizeDelta(grid);
    return badness;
};

const formGrid = (
    layout: GridLayout,
    base: number,
    fullMeta: Record<string, GridTileMeta>
): Grid => {
    let emptyTileCount = 0;
    const gridTilesMeta = [
        ...layout.flat().reduce((set, tile) => {
            if (tile === undefined || tile === EMPTY_TILE) emptyTileCount++;
            set.add(tile);
            return set;
        }, new Set<GridTile>()),
    ]
        .map<GridTileMeta | undefined>((tile) => {
            if (tile === undefined || tile === EMPTY_TILE) {
                return undefined;
            }
            return {
                ...fullMeta[tile],
                startRow: fullMeta[tile].startRow - base,
            };
        })
        .reduce<Record<string, GridTileMeta>>((acc, meta) => {
            if (meta !== undefined) {
                acc[meta.id] = meta;
            }
            return acc;
        }, {});

    return {
        layout,
        meta: {
            tiles: gridTilesMeta,
            emptyTileCount,
        },
    };
};

const intersect = <T>(a: Set<T>, b: Set<T>): Set<T> => {
    return new Set([...a].filter((x) => b.has(x)));
};

const sliceBoundaries = (layout: GridLayout): number[] => {
    const boundaryIdxs = [];
    let lastTiles = new Set<GridTile>();
    for (let i = 0; i < layout.length; i++) {
        const row = layout[i];
        const allTiles = row.filter(isTileFilled).reduce((all, item) => {
            all.add(item);
            return all;
        }, new Set<GridTile>());

        if (i !== 0) {
            if (intersect(allTiles, lastTiles).size === 0) {
                boundaryIdxs.push(i);
            }
        }
        lastTiles = allTiles;
    }
    return boundaryIdxs;
};

export const splitIntoFullBlocks = (grid: Grid | undefined): Array<Grid> => {
    if (grid === undefined) return [];

    const boundaries = sliceBoundaries(grid.layout);

    return splitAtIdxs(grid.layout, boundaries).map(([base, block]) =>
        formGrid(block, base, grid.meta.tiles)
    );
};

const splitAtIdxs = <A>(
    arr: Array<A>,
    idxs: Array<number>
): Array<[number, Array<A>]> => {
    if (idxs.length === 0) return [[0, arr]];

    const result: Array<[number, Array<A>]> = [];

    let lastIdx = 0;
    for (let i = 0; i < idxs.length; i++) {
        const idx = idxs[i];
        result.push([lastIdx, arr.slice(lastIdx, idx)]);
        lastIdx = idx;
    }
    if (lastIdx !== arr.length) {
        result.push([lastIdx, arr.slice(lastIdx, arr.length)]);
    }
    return result;
};

export const isSingleTileGrid = (grid: Grid): boolean => {
    return Object.keys(grid.meta.tiles).length + grid.meta.emptyTileCount === 1;
};

export const splitIntoChunks = <T>(arr: T[], chunkSize: number): T[][] => {
    if (!chunkSize) {
        throw new Error(`splitIntoChunks: invalid chunkSize: ${chunkSize}`);
    }
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
        const chunk = arr.slice(i, i + chunkSize);
        chunks.push(chunk);
    }
    return chunks;
};

export const createLayoutFromMeta = (
    meta: GridMeta,
    columnCount: number
): GridLayout => {
    const layout: GridLayout = [];
    Object.values(meta.tiles).forEach((tile) => {
        for (let r = tile.startRow; r < tile.startRow + tile.height; r++) {
            if (layout[r] === undefined) {
                layout[r] = getEmptyGridRow(columnCount);
            }
            for (let c = tile.startCol; c < tile.startCol + tile.width; c++) {
                layout[r][c] = tile.id;
            }
        }
    });
    return layout;
};

export const optimizeGrid = (slice: Grid): Grid => {
    const tilesArray = Object.values(slice.meta.tiles);
    if (tilesArray.length < 2) {
        return slice;
    }
    const uniqueSizes = new Set(
        tilesArray.map((tile) => tile.width * tile.height)
    );
    if (uniqueSizes.size === 1) {
        return slice;
    }

    const orderedTilesByArea = [...tilesArray].sort(
        (a, b) => b.width * b.height - a.width * a.height
    );
    const orderedTilesByScore = [...tilesArray].sort(
        (a, b) => b.score - a.score
    );

    const optimizedTiles = orderedTilesByScore.reduce<
        Record<string, GridTileMeta>
    >((acc, tile, idx) => {
        const { startCol, startRow, width, height } = orderedTilesByArea[idx];
        return {
            ...acc,
            [tile.id]: {
                ...tile,
                delta:
                    !tile.penalized && !tile.boosted
                        ? getSizeDeltaBySize(
                              width,
                              height,
                              tile.size,
                              getColumnCount(slice)
                          )
                        : tile.delta,
                startCol,
                startRow,
                width,
                height,
            },
        };
    }, {});

    const optimizedMeta: GridMeta = {
        tiles: optimizedTiles,
        emptyTileCount: slice.meta.emptyTileCount,
    };

    return {
        layout: createLayoutFromMeta(optimizedMeta, getColumnCount(slice)),
        meta: optimizedMeta,
    };
};

export const optimizeGridBlocks = (grid: Grid): Grid => {
    const slices = splitIntoFullBlocks(grid);
    const optimizedSlices = slices.map(optimizeGrid);
    return stackGrids(optimizedSlices);
};

export const stackGrids = (grids: Array<Grid> | undefined): Grid => {
    if (!grids || grids.length === 0) {
        return getEmptyGrid(0);
    }
    if (grids.length === 1) {
        return grids[0];
    }

    return grids.slice(1).reduce((stacked, grid) => {
        const rowCount = getRowCount(stacked);

        const stackedTiles = mapValues(
            grid.meta.tiles,
            (tile: GridTileMeta) => ({
                ...tile,
                startRow: tile.startRow + rowCount,
            })
        );

        return {
            layout: [...stacked.layout, ...grid.layout],
            meta: {
                tiles: {
                    ...stacked.meta.tiles,
                    ...stackedTiles,
                },
                emptyTileCount:
                    stacked.meta.emptyTileCount + grid.meta.emptyTileCount,
            },
        };
    }, grids[0]);
};

export const getChunkSize = (
    maxChunkSize: number,
    entitiesCount: number,
    gridSize: number
): number => {
    if (entitiesCount <= maxChunkSize) {
        return entitiesCount;
    }
    let candidate = maxChunkSize;
    while (candidate > 1) {
        const remainder = entitiesCount % candidate;
        if (remainder === 0 || remainder >= gridSize) {
            return candidate;
        }
        candidate--;
    }
    return maxChunkSize;
};
